import { useSubscription } from "@/contexts/SubscriptionContext";
import { Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * ProGate — wraps content that requires Pro subscription.
 * When user is not Pro and paywall is enabled, shows a lock overlay.
 * 
 * Usage:
 *   <ProGate feature="signatures">
 *     <SignatureGenerator />
 *   </ProGate>
 * 
 * Or for inline content:
 *   <ProGate feature="achievement_notes" inline>
 *     <NoteEditor />
 *   </ProGate>
 */
export default function ProGate({ feature, children, inline = false, message }) {
  const { requirePro, paywallEnabled } = useSubscription();
  const navigate = useNavigate();

  // If paywall is off or user is Pro, render children normally
  if (!paywallEnabled || requirePro(feature)) {
    return <>{children}</>;
  }

  const defaultMessage = "This feature requires Game-o-Rang Pro";

  if (inline) {
    return (
      <div
        className="relative group cursor-pointer"
        onClick={() => navigate("/app/upgrade")}
        data-testid={`pro-gate-${feature}`}
      >
        <div className="opacity-40 pointer-events-none select-none blur-[1px]">
          {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex items-center gap-1.5 bg-black/80 border border-purple-500/40 rounded-full px-3 py-1.5 text-xs text-purple-300 backdrop-blur-sm group-hover:border-purple-400 transition-colors">
            <Lock className="w-3 h-3" />
            <span>Pro</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative rounded-lg overflow-hidden cursor-pointer group"
      onClick={() => navigate("/app/upgrade")}
      data-testid={`pro-gate-${feature}`}
    >
      <div className="opacity-30 pointer-events-none select-none blur-[2px]">
        {children}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-[1px]">
        <div className="flex flex-col items-center gap-3 p-6 rounded-xl bg-zinc-900/90 border border-purple-500/30 shadow-lg shadow-purple-500/5 group-hover:border-purple-400/50 transition-all max-w-xs text-center">
          <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
            <Lock className="w-5 h-5 text-purple-400" />
          </div>
          <p className="text-white font-semibold text-sm">{message || defaultMessage}</p>
          <span className="text-xs text-purple-400 group-hover:text-purple-300 transition-colors">
            Click to upgrade
          </span>
        </div>
      </div>
    </div>
  );
}
